<template>
    <div ref="wrapper" class="plotly-chart" :style="{ height: resolvedHeight }">
        <div v-if="hasError" class="plotly-chart-error">
            <q-icon name="error_outline" size="14px" class="q-mr-xs"/>
            <div>Chart could not be rendered</div>
        </div>
        <div v-else ref="plotlyDiv" style="width: 100%; height: 100%;"></div>
    </div>
</template>

<script>
import Plotly from "plotly.js-dist";
import { parsePlotlyFigure } from "@/chatDownloads.js";

// Everything that differs between the in-chat thumbnail and the expanded popup.
const SIZE_PRESETS = {
    small: {
        height: "300px",
        fontSize: 8,
        titleFontSize: 10,
        margin: { l: 5, r: 5, t: 60, b: 20 },
        displayModeBar: false,
        staticPlot: false,
    },
    large: {
        height: "auto",
        fontSize: 11,
        titleFontSize: 14,
        margin: { l: 65, r: 30, t: 40, b: 55 },
        displayModeBar: false,
        staticPlot: false,
    },
};

export default {
    name: "PlotlyChart",

    props: {
        // A plotly figure json ({ data, layout, config }), a bare list of traces,
        // or the json string of either as it comes back from the DB.
        chartData: {
            type: [Object, Array, String],
            required: true,
        },
        size: {
            type: String,
            default: "large",
            validator: (value) => Object.keys(SIZE_PRESETS).includes(value),
        },
        // Optional css height override, e.g. "220px".
        height: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            hasError: false,
        };
    },

    computed: {
        preset() {
            return SIZE_PRESETS[this.size] || SIZE_PRESETS.large;
        },
        resolvedHeight() {
            return this.height || this.preset.height;
        },
    },

    mounted() {
        this.renderChart();
        this.startResizeObserver();
    },

    beforeUnmount() {
        this.stopResizeObserver();
        this.purge();
    },

    watch: {
        chartData: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
        },
        size() {
            // Layout/config differ per size, so rebuild from scratch.
            this.purge();
            this.$nextTick(() => {
                this.renderChart();
            });
        },
    },

    methods: {
        parseFigure() {
            return parsePlotlyFigure(this.chartData);
        },

        buildLayout(layout) {
            const responsiveLayout = {
                ...layout,
                autosize: true,
                width: undefined,
                height: undefined,
                margin: { ...this.preset.margin, ...(layout.margin || {}) },
                font: { size: this.preset.fontSize, ...(layout.font || {}) },
            };

            if (layout.title) {
                const title = typeof layout.title === "string" ? { text: layout.title } : { ...layout.title };
                title.font = { size: this.preset.titleFontSize, ...(title.font || {}) };
                responsiveLayout.title = title;
            }

            if (layout.showlegend !== false) {
                responsiveLayout.legend = {
                    ...(layout.legend || {}),
                    font: { size: this.preset.fontSize, ...((layout.legend || {}).font || {}) },
                };
            }

            return responsiveLayout;
        },

        buildConfig(config) {
            return {
                responsive: true,
                displaylogo: false,
                displayModeBar: this.preset.displayModeBar,
                staticPlot: this.preset.staticPlot,
                modeBarButtonsToRemove: ["lasso2d", "select2d"],
                ...config,
            };
        },

        renderChart() {
            if (!this.chartData) return;

            let figure;
            try {
                figure = this.parseFigure();
            } catch (error) {
                console.error("Invalid plotly chart json:", error);
                this.hasError = true;
                return;
            }

            this.hasError = false;

            this.$nextTick(() => {
                const div = this.$refs.plotlyDiv;
                if (!div) return;

                const layout = this.buildLayout(figure.layout);
                const config = this.buildConfig(figure.config);

                try {
                    // Use react instead of newPlot to avoid recreating the entire plot
                    if (div.data) {
                        Plotly.react(div, figure.data, layout, config);
                    } else {
                        Plotly.newPlot(div, figure.data, layout, config);
                    }
                } catch (error) {
                    console.error("Error rendering plotly chart:", error);
                    this.hasError = true;
                }
            });
        },

        // The chat panel toggles width and the popup opens with a transition,
        // neither of which fire a window resize event.
        startResizeObserver() {
            if (typeof ResizeObserver === "undefined" || !this.$refs.wrapper) return;

            this.resizeObserver = new ResizeObserver(() => {
                if (this.resizeFrame) {
                    cancelAnimationFrame(this.resizeFrame);
                }
                this.resizeFrame = requestAnimationFrame(() => {
                    const div = this.$refs.plotlyDiv;
                    if (div && div.data && div.clientWidth > 0) {
                        Plotly.Plots.resize(div);
                    }
                });
            });
            this.resizeObserver.observe(this.$refs.wrapper);
        },

        stopResizeObserver() {
            if (this.resizeFrame) {
                cancelAnimationFrame(this.resizeFrame);
                this.resizeFrame = null;
            }
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
        },

        purge() {
            if (this.$refs.plotlyDiv) {
                Plotly.purge(this.$refs.plotlyDiv);
            }
        },
    },
};
</script>

<style scoped>
.plotly-chart {
    width: 100%;
    max-width: 100%;
    min-width: 0;
}

.plotly-chart-error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #b02a1a;
    font-size: 10px;
}
</style>
